import { useMemo } from 'react';
import type { Asset } from '@/types/GraphqlSchema';
import { titleize } from '@/utils/helpers';
import type { CompanyParticipation, Metric } from '../types';

const getLatestParticipations = (asset: Asset | null) => {
  if (!asset?.participations || asset.participations.length === 0) {
    return null;
  }

  const latestEffectiveDate = asset.participations[0]?.effectiveDate;
  return asset.participations.filter(
    participation => participation.effectiveDate === latestEffectiveDate && participation.company,
  );
};

const getOperators = (asset: Asset | null): CompanyParticipation[] | null => {
  const latestParticipations = getLatestParticipations(asset);
  if (!latestParticipations) {
    return null;
  }

  const operators = latestParticipations
    .filter(participation => participation.isCompanyOperator)
    .map(participation => ({
      text: `${participation.company?.name} (${participation.formattedPercentStake})`,
      needsIcon: participation.mAndAOpportunity ?? false,
    }));

  return operators.length > 0 ? operators : null;
};

const getPartners = (asset: Asset | null): CompanyParticipation[] | null => {
  const latestParticipations = getLatestParticipations(asset);
  if (!latestParticipations) {
    return null;
  }

  const partners = latestParticipations
    .filter(participation => !participation.isCompanyOperator)
    .sort(
      (a, b) =>
        (b.percentStake ?? 0) - (a.percentStake ?? 0) ||
        (a.company?.name ?? '').localeCompare(b.company?.name ?? ''),
    )
    .map(participation => ({
      text: `${participation.company?.name} (${participation.formattedPercentStake})`,
      needsIcon: participation.mAndAOpportunity ?? false,
    }));

  return partners.length > 0 ? partners : null;
};

export const useKeyInformationMetrics = (asset: Asset | null): Metric[] => {
  return useMemo(() => {
    if (!asset) {
      return [];
    }

    return [
      {
        label: 'Country / Area',
        value: asset.country?.displayName ?? null,
      },
      {
        label: 'Operator',
        value: getOperators(asset) ?? null,
      },
      {
        label: 'Partners',
        value: getPartners(asset) ?? null,
      },
      {
        label: 'Basin',
        value: asset.basin?.name ?? null,
      },
      {
        label: 'Primary Hydrocarbon',
        value: asset.hydrocarbonType ? titleize(asset.hydrocarbonType) : null,
      },
      {
        label: 'HPHT',
        value: asset.isHpht ? 'Yes' : asset.isHpht === false ? 'No' : null,
      },
      {
        label: 'Shore Status',
        value: asset.shoreStatus?.name ?? null,
      },
      {
        label: 'Development Status',
        value: asset.developmentStatus?.name ?? null,
      },
      {
        label: 'Development Type',
        value: asset.developmentType?.name ?? null,
      },
    ];
  }, [asset]);
};
